import { useState } from "react";
import { Sparkles } from "lucide-react";
import { getStats, getHearts, refillHearts, buyStreakFreeze, getGems } from "../gamification";
import { playCorrect, playXP } from "../sounds";

export default function Shop() {
  const [stats, setStats] = useState(getStats);
  const [message, setMessage] = useState(null);

  function refresh() { setStats(getStats()); }

  function handleRefillHearts() {
    if (refillHearts()) { setMessage("Hearts refilled!"); playCorrect(); refresh(); }
    else { setMessage("Not enough gems!"); }
    setTimeout(() => setMessage(null), 2000);
  }

  function handleBuyFreeze() {
    if (buyStreakFreeze()) { setMessage("Streak freeze purchased!"); playXP(); refresh(); }
    else { setMessage("Not enough gems! Need 100."); }
    setTimeout(() => setMessage(null), 2000);
  }

  const hearts = getHearts();
  const gems = getGems();

  return (
    <div className="space-y-4 page-enter relative">
      <div className="blob blob-cyan" style={{ width: 320, height: 320, top: -40, right: -40, opacity: 0.18 }} />
      <div className="blob blob-purple" style={{ width: 260, height: 260, top: 280, left: -40, opacity: 0.15 }} />
      <div className="relative">
        <h1 className="font-display text-4xl md:text-5xl font-normal"><em>Shop.</em></h1>
        <p className="text-gray-600 dark:text-gray-300 font-semibold mt-1">Spend your gems to power up</p>
      </div>

      {/* Gems balance */}
      <div className="gradient-bg-primary rounded-2xl p-4 text-ink-900 text-center relative overflow-hidden card-hover">
        <div className="absolute top-0 right-0 w-40 h-40 bg-paper-deep rounded-full -mr-10 -mt-10" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-paper-deep rounded-full -ml-6 -mb-3" />
        <div className="relative">
          <div className="text-2xl mb-2 float">💎</div>
          <p className="text-3xl font-black count-up">{gems}</p>
          <p className="text-paper text-sm mt-1">Gems Available</p>
        </div>
      </div>

      {message && (
        <div className="bg-sage-light border border-sage-light text-sage-dark rounded-xl p-3 text-sm font-bold text-center bounce-in">
          {message}
        </div>
      )}

      {/* Shop items */}
      <div className="space-y-4">
        {/* Refill Hearts */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 card-hover fade-in stagger-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-berry-light to-rust-light rounded-2xl flex items-center justify-center text-2xl">
                ❤️
              </div>
              <div>
                <h3 className="font-black text-gray-900">Refill Hearts</h3>
                <p className="text-sm text-gray-400">Restore all 5 hearts</p>
                <div className="flex gap-1 mt-1">
                  {[1,2,3,4,5].map((h) => (
                    <span key={h} className={`text-sm ${h <= hearts ? "text-berry" : "text-gray-200"}`}>♥</span>
                  ))}
                </div>
              </div>
            </div>
            <button onClick={handleRefillHearts} disabled={hearts >= 5}
              className="btn-3d bg-gold-100 text-gold-700 px-5 py-2.5 font-black text-sm flex items-center gap-1.5 disabled:opacity-30 hover:bg-gold-200">
              💎 50
            </button>
          </div>
        </div>

        {/* Streak Freeze */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 card-hover fade-in stagger-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-sky-light to-sky-light rounded-2xl flex items-center justify-center text-2xl">
                🛡️
              </div>
              <div>
                <h3 className="font-black text-gray-900">Streak Freeze</h3>
                <p className="text-sm text-gray-400">Protect streak for 1 missed day</p>
                <p className="text-xs text-sky-dark font-bold mt-1">Owned: {stats.streakFreezes || 0}</p>
              </div>
            </div>
            <button onClick={handleBuyFreeze}
              className="btn-3d bg-gold-100 text-gold-700 px-5 py-2.5 font-black text-sm flex items-center gap-1.5 hover:bg-gold-200">
              💎 100
            </button>
          </div>
        </div>

        {/* Double XP (coming soon) */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 opacity-50 fade-in stagger-3 relative">
          <div className="ribbon">SOON</div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-gold-100 to-gold-100 rounded-2xl flex items-center justify-center text-2xl">
                ⚡
              </div>
              <div>
                <h3 className="font-black text-gray-900">Double XP Boost</h3>
                <p className="text-sm text-gray-400">2x XP for 1 hour</p>
              </div>
            </div>
            <div className="bg-gray-100 text-gray-400 px-5 py-2.5 rounded-xl font-black text-sm">
              💎 200
            </div>
          </div>
        </div>
      </div>

      {/* How to earn */}
      <div className="bg-gradient-to-br from-gray-50 to-paper-deep rounded-2xl p-6 border border-ink-100 fade-in">
        <h3 className="font-black text-gray-900 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-gold-700" /> How to Earn Gems
        </h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { emoji: "📖", text: "Complete lessons", gems: "+1-5" },
            { emoji: "📝", text: "Pass tests", gems: "+2-5" },
            { emoji: "💬", text: "Conversation practice", gems: "+2" },
            { emoji: "🔥", text: "Daily streak bonus", gems: "+3/day" },
            { emoji: "🎮", text: "Mini games", gems: "+1-3" },
            { emoji: "📝", text: "Vocabulary review", gems: "+1" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 bg-white rounded-xl p-3 hover-lift">
              <span className="text-xl">{item.emoji}</span>
              <span className="text-sm text-gray-600 flex-1">{item.text}</span>
              <span className="text-xs font-black text-gold-700 bg-paper-deep px-2 py-1 rounded-full">{item.gems}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
